/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import type { Options } from "react-hotkeys-hook"
import type {
	HotkeyCallback,
	Keys,
	OptionsOrDependencyArray,
} from "react-hotkeys-hook/dist/types"
import { useHotkeys } from "react-hotkeys-hook"

import { isSequenceInProgress, markSequencePrefix } from "./shortcut-sequence"

/**
 * Get an object property using a string object notation
 * Example: const obj = {a: b: {c: "hi"}} => get(obj, "a.b.c") = "hi"
 * From here: https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_get
 */
export const get = (obj: any, path: string, defaultValue = undefined) => {
	const travel = (regexp: RegExp) =>
		String.prototype.split
			.call(path, regexp)
			.filter(Boolean)
			.reduce(
				(res, key) => (res !== null && res !== undefined ? res[key] : res),
				obj,
			)
	const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/)
	return result === undefined || result === obj ? defaultValue : result
}

export function isRadixFocusGuardOpen() {
	if (typeof document !== "undefined") {
		const focusGuard = document.querySelector("[data-radix-focus-guard]")
		if (focusGuard) {
			return true
		}
	}
	return false
}

// The single source of truth for "is this keystroke prose?" Only relevant to
// keys that could be text: a single printable character with no command
// modifier. Modifier combos (⌘↵) and named control keys (Escape, arrows) can
// never be prose, so the answer is always no for them — the guard never
// touches them, which is how send (⌘↵) and blur (esc) keep working inside the
// composer.
function isProseKey(e: KeyboardEvent): boolean {
	if (e.metaKey || e.ctrlKey || e.altKey) return false
	return e.key.length === 1
}

// Reads `event.target`, not `document.activeElement`, so it is correct in the
// same synchronous tick the keydown fires — even after a send clears and blurs
// the editor. Previous guards (isContentEditableFocused, typing-guard,
// useGuardedShortcut) all tried to infer this from focus state a frame later
// and each needed its own timer to bridge the gap.
function eventIsTextEntry(e: KeyboardEvent): boolean {
	if (!isProseKey(e)) return false
	const node = e.target
	if (!(node instanceof HTMLElement)) return false

	// Real form fields. This is the check react-hotkeys-hook runs natively for
	// input/textarea/select; we run our own so the same rule covers
	// contenteditable below.
	if (node.closest("input, textarea, select")) return true

	// Any contenteditable except an explicit "false". Matches the bare
	// attribute and ""/"true"/"plaintext-only" — Tiptap renders the bare
	// attribute, which the === "true" comparison this replaces could miss.
	if (node.closest('[contenteditable]:not([contenteditable="false"])')) {
		return true
	}

	return false
}

if (typeof window !== "undefined") {
	// Capture phase: react-hotkeys-hook listens on document, so the prefix has to
	// be recorded before the single-key layer sees the next keydown.
	window.addEventListener(
		"keydown",
		(e) => {
			markSequencePrefix(e.key)
		},
		true,
	)
}

interface ShortcutGuards {
	// Drops the shared guards below. Only for modifier combos that have to fire
	// inside a Radix overlay; a bare key must never set it.
	skipDefaultGuards?: boolean
}

export interface ShortcutOptions extends Options, ShortcutGuards {}

// The single printable characters that can collide with prose. Only these get
// the strict options type below.
type PrintableKey =
	| "a"
	| "b"
	| "c"
	| "d"
	| "e"
	| "f"
	| "g"
	| "h"
	| "i"
	| "j"
	| "k"
	| "l"
	| "m"
	| "n"
	| "o"
	| "p"
	| "q"
	| "r"
	| "s"
	| "t"
	| "u"
	| "v"
	| "w"
	| "x"
	| "y"
	| "z"

// Enforces the `skipDefaultGuards` rule at compile time. When `keys` is a
// single printable character, the option is typed `never`; modifier combos and
// named keys keep it. A generic conditional on a single signature, because
// TypeScript's overload fallback would let a bad call slip through.
export type ShortcutOptionsFor<K extends Keys> = K extends PrintableKey
	? Omit<ShortcutOptions, "skipDefaultGuards"> & { skipDefaultGuards?: never }
	: ShortcutOptions

export function useShortcut<K extends Keys>(
	keys: K,
	callback: HotkeyCallback,
	options?: ShortcutOptionsFor<K>,
	dependencies?: OptionsOrDependencyArray,
): void {
	const {
		skipDefaultGuards,
		ignoreEventWhen,
		...hotkeyOptions
	}: ShortcutOptions = options ?? {}
	useHotkeys(
		keys,
		callback,
		{
			...hotkeyOptions,
			// A caller's own check adds to the guards; it can never remove them.
			ignoreEventWhen: (event) => {
				if (ignoreEventWhen?.(event)) return true
				if (skipDefaultGuards) return false
				if (isRadixFocusGuardOpen()) return true
				// Sequences ("g s") are matched by tinykeys on the same keydown; the
				// single-key layer must not also fire off the second key.
				if (isSequenceInProgress()) return true
				if (eventIsTextEntry(event)) return true
				return false
			},
		},
		dependencies,
	)
}

async function sleep(ms: number) {
	return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

export async function minDelay<T>(promise: Promise<T>, ms: number) {
	const [p] = await Promise.all([promise, sleep(ms)])

	return p
}
